import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';

const navLinks = [
  { label: 'What We Offer', to: '/what-we-offer' },
  { label: 'Industries', to: '/industries' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handler);
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const goHome = () => {
    if (location.pathname !== '/') {
      navigate('/');
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const useSolidNavbar = scrolled || isLoginPage;

  const linkColor = useSolidNavbar
    ? 'text-gray-800 hover:text-blue-600'
    : 'text-white/90 hover:text-white';

  const secondaryBtnClasses = useSolidNavbar
    ? 'border-gray-400/70 text-gray-700 hover:border-blue-600 hover:text-blue-600'
    : 'border-white/50 text-white hover:border-white';

  const navClasses = useSolidNavbar
    ? 'bg-white/75 backdrop-blur-md shadow-md border-b border-white/20'
    : 'bg-transparent';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navClasses}`}
    >
      <div className="mx-auto h-[4.5rem] max-w-6xl px-5 sm:h-20 sm:px-6 lg:px-8">
        <div className="flex h-full items-center gap-3 md:gap-4">
          <div className="flex min-w-0 shrink-0 items-center justify-start">
            <button
              onClick={goHome}
              className="flex items-center transition-opacity hover:opacity-80"
              aria-label="Scroll to top"
            >
              <Logo scrolled={scrolled} forceWhite={!scrolled} compact />
            </button>
          </div>

          <div className="hidden min-w-0 flex-1 items-center justify-start gap-5 pl-6 lg:flex xl:gap-6 xl:pl-8">
            {navLinks.map((l) => (
              <button
                key={l.to}
                onClick={() => {
                  navigate(l.to);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${linkColor}`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="hidden shrink-0 items-center justify-end gap-2.5 lg:ml-auto lg:flex xl:gap-3">
            <button
              onClick={() => navigate('/login')}
              className={`text-sm font-bold px-4 py-1.5 rounded-full border transition-colors whitespace-nowrap ${secondaryBtnClasses}`}
            >
              Login
            </button>

            <Link
              to="/partner"
              className="cta-secondary whitespace-nowrap px-5 py-2"
            >
              Partner with Us
            </Link>

            <Link
              to="/apply"
              className="cta-primary whitespace-nowrap px-5 py-2"
            >
              Apply for Funding
            </Link>
          </div>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            className={`lg:hidden rounded-full p-2 transition-colors ${useSolidNavbar ? 'text-gray-800' : 'text-white'}`}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-gray-900/95 backdrop-blur-md"
          >
            <div className="flex flex-col gap-3 px-5 py-4 sm:px-6 sm:py-5">
              {navLinks.map((l) => (
                <button
                  key={l.to}
                  onClick={() => {
                    navigate(l.to);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-white/90 font-bold text-base hover:text-amber-400 transition-colors cursor-pointer text-left"
                >
                  {l.label}
                </button>
              ))}
              <hr className="border-white/10" />
              <Link
                to="/partner"
                className="cta-secondary block text-center"
              >
                Partner with Us
              </Link>
              <Link
                to="/apply"
                className="cta-primary text-center"
              >
                Apply for Funding
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
